import type { AnalyticsEvent } from './events'
import {
  ANALYTICS_SCHEMA_VERSION,
  MAX_ANALYTICS_BATCH_SIZE,
  isSafeAnalyticsSessionId,
  toRemoteAnalyticsEvent,
  type RemoteAnalyticsBatch,
  type RemoteAnalyticsEvent,
} from './remoteSchema'
import { getDeviceInfo, type DeviceInfo } from '../utils/device'

const DEFAULT_ENDPOINT = '/api/analytics'
const DEFAULT_FLUSH_DELAY_MS = 4_000

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export interface AnalyticsTransport {
  enqueue(event: AnalyticsEvent): void
}

export interface CloudflareAnalyticsTransportOptions {
  endpoint?: string
  fetcher?: Fetcher
  device?: DeviceInfo
  flushDelayMs?: number
  lifecycle?: boolean
}

function fallbackSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

export class CloudflareAnalyticsTransport implements AnalyticsTransport {
  private readonly endpoint: string
  private readonly fetcher: Fetcher
  private readonly device: DeviceInfo
  private readonly flushDelayMs: number
  private queue: RemoteAnalyticsEvent[] = []
  private sessionId = fallbackSessionId()
  private appVersion = 'unknown'
  private flushTimer: ReturnType<typeof setTimeout> | undefined
  private flushing = false

  constructor(options: CloudflareAnalyticsTransportOptions = {}) {
    this.endpoint = options.endpoint ?? DEFAULT_ENDPOINT
    this.fetcher = options.fetcher ?? ((input, init) => fetch(input, init))
    this.device = options.device ?? getDeviceInfo()
    this.flushDelayMs = options.flushDelayMs ?? DEFAULT_FLUSH_DELAY_MS

    if (options.lifecycle !== false && typeof document !== 'undefined' && typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flushOnExit()
      })
      window.addEventListener('pagehide', () => this.flushOnExit())
    }
  }

  enqueue(event: AnalyticsEvent): void {
    const metadataSessionId = event.metadata.sessionId
    if (isSafeAnalyticsSessionId(metadataSessionId)) this.sessionId = metadataSessionId
    if (typeof event.metadata.version === 'string' && event.metadata.version.length <= 32) {
      this.appVersion = event.metadata.version
    }

    this.queue = [...this.queue, toRemoteAnalyticsEvent(event)].slice(-MAX_ANALYTICS_BATCH_SIZE * 2)
    if (this.queue.length >= MAX_ANALYTICS_BATCH_SIZE) {
      void this.flush()
      return
    }
    this.scheduleFlush()
  }

  get pendingCount(): number {
    return this.queue.length
  }

  dispose(): void {
    this.clearFlushTimer()
  }

  async flush(): Promise<void> {
    if (this.flushing || this.queue.length === 0) return
    this.clearFlushTimer()
    this.flushing = true
    const events = this.queue.splice(0, MAX_ANALYTICS_BATCH_SIZE)
    try {
      const response = await this.fetcher(this.endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-KVIBE-Analytics': '1',
        },
        body: JSON.stringify(this.batch(events)),
        keepalive: true,
      })
      if (!response.ok) throw new Error('ANALYTICS_DELIVERY_FAILED')
    } catch {
      // Analytics must never block the mobility flow. Keep one bounded retry
      // batch in memory without logging event data or leaking it to another sink.
      this.queue = [...events, ...this.queue].slice(-MAX_ANALYTICS_BATCH_SIZE * 2)
    } finally {
      this.flushing = false
      if (this.queue.length > 0) this.scheduleFlush()
    }
  }

  private flushOnExit(): void {
    if (this.flushing || this.queue.length === 0) return
    this.clearFlushTimer()
    const events = this.queue.splice(0, MAX_ANALYTICS_BATCH_SIZE)
    const payload = JSON.stringify(this.batch(events))
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const accepted = navigator.sendBeacon(this.endpoint, new Blob([payload], { type: 'application/json' }))
      if (accepted) return
    }
    void this.fetcher(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-KVIBE-Analytics': '1' },
      body: payload,
      keepalive: true,
    }).catch(() => undefined)
  }

  private batch(events: RemoteAnalyticsEvent[]): RemoteAnalyticsBatch {
    return {
      schemaVersion: ANALYTICS_SCHEMA_VERSION,
      sessionId: this.sessionId,
      appVersion: this.appVersion,
      device: this.device,
      events,
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== undefined) return
    this.flushTimer = setTimeout(() => {
      this.flushTimer = undefined
      void this.flush()
    }, this.flushDelayMs)
  }

  private clearFlushTimer(): void {
    if (this.flushTimer === undefined) return
    clearTimeout(this.flushTimer)
    this.flushTimer = undefined
  }
}
